import React, { useCallback, useMemo } from 'react';
import Modal from '@active/react-ui/Modal';
import ModalBody from '@active/react-ui/ModalBody';
import ModalFooter from '@active/react-ui/ModalFooter';
import Button from '@active/react-ui/Button';
import Alert from '@active/react-ui/Alert';
import Grid from '@active/react-ui/Grid';
import GridItem from '@active/react-ui/GridItem';
import FormGroup from '@active/react-ui/FormGroup';
import FormLabel from '@active/react-ui/FormLabel';
import FormField from '@active/react-ui/FormField';
import Input from '@active/react-ui/Input';
import ModalHeader from '@active/react-ui/ModalHeader';
import { useLocale, Locale } from '@active/react-utils/i18n';
import Form from '@active/react-ui/Form';
import ImageScanner from '../../../../shared/imgs/check/img-scanner.gif';
import { useCheckScanner } from '../hooks/useCheckScanner';
import { SCANSTATUS } from '../config';

export const CheckScannerModal = () => {
  const { i18n } = useLocale();
  const {
    showModal,
    setShowModal,
    scanInfo,
    setScanInfo,
    saveCallback,
    processDocument,
  } = useCheckScanner();

  const {
    scannedCheckNumber, frontImage, backImage, scanStatus,
  } = scanInfo;

  const checkNumberValidator = useCallback((value) => {
    const reg = /^\w{1,100}$/;
    if (!reg.test(value)) {
      return i18n('checkout.lib.components.payment.check.valid.checkNumber');
    }
    return '';
  }, [ i18n ]);

  const handleSubmit = useCallback(() => {
    setShowModal(false);
    saveCallback(scanInfo);
  }, [ scanInfo, setShowModal, saveCallback ]);

  const reScan = useCallback(() => {
    processDocument({ endorsementContent: '' });
  }, [ processDocument ]);

  const isInitStatus = useMemo(() => scanStatus === SCANSTATUS.init, [ scanStatus ]);
  const handleCloseModal = useCallback(() => {
    if (isInitStatus) return;
    setShowModal(false);
  }, [ setShowModal, isInitStatus ]);

  return (
    <Modal
      title={i18n('checkout.lib.components.payment.check.checkScannerModal.title')}
      className="check-scanner-modal"
      shown={showModal}
      style={{ margin: 'auto' }}
      size='md'
      customizeHeader={isInitStatus }
      onClose={handleCloseModal}>
      {isInitStatus && <ModalHeader>
        <h3 className="modal-title">
          <Locale id="checkout.lib.components.payment.check.checkScannerModal.title"/>
        </h3>
      </ModalHeader>}
      <ModalBody>
        {isInitStatus ? <div className="check-scanner-loading-box">
          <Alert theme="inline" className="check-warning">
            <Locale id="checkout.lib.components.payment.check.scanReady"/>
          </Alert>
          <div className="check-scanner-img-box">
            <img src={ImageScanner} alt="check-scanner-loading" />
            <span className="check-scanner-loading-text">
              <Locale id="checkout.lib.components.payment.check.scanning"/>
            </span>
          </div>
        </div>
          : <div>
            <Form
              customizeLayout
              initialValues={{
                scannedCheckNumber,
              }}
              id="check-scanner-form"
              onSubmit={handleSubmit}>
              <Grid container={false} rowGap={0} colGap={0} >
                <GridItem span={12} spanXl={8}>
                  <FormGroup name="scannedCheckNumber" required>
                    <FormLabel >
                      <Locale id="checkout.lib.components.payment.check.checkNumber"/>
                    </FormLabel>
                    <FormField
                      maxLength="100"
                      autoComplete="off"
                      validate={checkNumberValidator}
                      onChange={(e) => {
                        setScanInfo({
                          ...scanInfo,
                          scannedCheckNumber: e.target.value,
                        });
                      }}
                      as={Input} />
                  </FormGroup>
                </GridItem>
              </Grid>
            </Form>
            <div className="check-scanner-title">
              <Locale id="checkout.lib.components.payment.check.digital"/>
            </div>
            <div>
              <img className="check-scanner-img" src={frontImage} alt="check-scanner-front" />
              <img className="check-scanner-img" src={backImage} alt="check-scanner-back" />
            </div>
          </div>
        }
      </ModalBody>
      <ModalFooter className={`modal-footer check-scanner-footer ${isInitStatus && 'check-scanner-opacity'}`} >
        <Button
          type="secondary"
          className="cancel-btn"
          disabled={isInitStatus}
          onClick={handleCloseModal}>
          <Locale id="checkout.lib.components.common.btn.cancel"/>
        </Button>
        <div>
          <Button
            type="secondary"
            className="reScan-btn"
            disabled={isInitStatus}
            onClick={reScan}>
            <Locale id="checkout.lib.components.common.btn.reScan"/>
          </Button>
          <Button
            className="submit-btn"
            type="strong"
            form="check-scanner-form"
            disabled={!scannedCheckNumber || isInitStatus}
            style={{ marginLeft: '20px' }}
            submit>
            <Locale id="checkout.lib.components.common.btn.save"/>
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};
